import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Images } from "lucide-react";

export default function ArticleGallery({ items = [], className = "" }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (src) => (src && src.startsWith("/") ? `${backend}${src}` : src);
  const [emblaRef, embla] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(0);

  const onSelect = useCallback(() => {
    if (embla) setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    setCount(embla.scrollSnapList().length);
    onSelect();
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    return () => {
      embla.off("select", onSelect);
      embla.off("reInit", onSelect);
    };
  }, [embla, onSelect]);

  if (!items || items.length === 0) return null;
  const multi = items.length > 1;

  return (
    <section className={`my-8 ${className}`} data-testid="article-gallery">
      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
        <Images className="h-4 w-4" />
        Галерия ({items.length})
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-slate-900" ref={emblaRef}>
        <div className="flex">
          {items.map((g, i) => (
            <div key={i} className="relative min-w-0 flex-[0_0_100%]">
              <img
                src={resolve(g.url)}
                alt={g.alt || ""}
                loading="lazy"
                decoding="async"
                className="aspect-video w-full object-cover"
              />
              {g.alt && (
                <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 text-sm text-white">
                  {g.alt}
                </p>
              )}
            </div>
          ))}
        </div>

        {multi && (
          <>
            <button
              type="button"
              onClick={() => embla?.scrollPrev()}
              data-testid="gallery-prev"
              aria-label="Предишна снимка"
              className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-slate-800 shadow transition hover:bg-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => embla?.scrollNext()}
              data-testid="gallery-next"
              aria-label="Следваща снимка"
              className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-slate-800 shadow transition hover:bg-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div
              className="absolute right-3 top-3 rounded-full bg-black/50 px-2.5 py-1 text-xs font-semibold text-white"
              data-testid="gallery-counter"
            >
              {selected + 1} / {count}
            </div>
          </>
        )}
      </div>

      {multi && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {items.map((g, i) => (
            <button
              key={i}
              type="button"
              onClick={() => embla?.scrollTo(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Снимка ${i + 1}`}
              className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg ring-2 transition ${
                selected === i ? "ring-primary" : "ring-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img src={resolve(g.url)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
